import React from 'react';
import { motion } from 'framer-motion';
import { 
  Cpu, 
  Workflow, 
  Shield, 
  Zap, 
  GitBranch, 
  BarChart3,
  Layers,
  RefreshCw,
  Lock,
  Globe
} from 'lucide-react';

const features = [
  {
    icon: Cpu,
    title: 'Autonomous Reasoning',
    description: 'AI agents that think before they act. Context-aware decision making with explainable logic chains.',
    span: 'col-span-1 md:col-span-2 row-span-1',
    gradient: 'from-indigo-500/20 to-purple-500/10',
  },
  {
    icon: Workflow,
    title: 'Visual Workflow Builder',
    description: 'Drag, drop, deploy. Build complex automations without code.',
    span: 'col-span-1 row-span-1',
    gradient: 'from-cyan-500/20 to-blue-500/10',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'SOC 2 Type II certified. End-to-end encryption. Your data never leaves your cloud.',
    span: 'col-span-1 row-span-1',
    gradient: 'from-green-500/20 to-emerald-500/10',
  },
  {
    icon: GitBranch,
    title: 'Conditional Logic',
    description: 'Build branching workflows with if/then conditions, loops, and error handling.',
    span: 'col-span-1 row-span-2',
    gradient: 'from-orange-500/20 to-amber-500/10',
    tall: true,
  },
  {
    icon: BarChart3,
    title: 'Real-Time Analytics',
    description: 'Monitor agent performance, track success rates, and optimize workflows.',
    span: 'col-span-1 row-span-1',
    gradient: 'from-pink-500/20 to-rose-500/10',
  },
  {
    icon: RefreshCw,
    title: 'Auto-Retry & Recovery',
    description: 'Intelligent failure handling with automatic retries and graceful fallbacks.',
    span: 'col-span-1 row-span-1',
    gradient: 'from-violet-500/20 to-purple-500/10',
  },
  {
    icon: Layers,
    title: 'Multi-Agent Orchestration',
    description: 'Coordinate multiple AI agents working together on complex tasks.',
    span: 'col-span-1 md:col-span-2 row-span-1',
    gradient: 'from-blue-500/20 to-indigo-500/10',
  },
];

const FeatureCard = ({ icon: Icon, title, description, span, gradient, tall, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      viewport={{ once: true }}
      className={`group relative overflow-hidden rounded-2xl p-6 md:p-8 bg-slate-900/40 border border-emerald-500/10 hover:border-emerald-500/30 transition-all duration-300 hover:bg-slate-900/60 ${span}`}
      data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Background gradient on hover */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      
      {/* Content */}
      <div className="relative z-10">
        <motion.div
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="w-12 h-12 rounded-xl bg-slate-800/50 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:border-emerald-500/30 transition-colors"
        >
          <Icon className="w-6 h-6 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
        </motion.div>
        
        <h3 className="text-lg md:text-xl font-bold font-outfit text-white mb-2 group-hover:text-gradient transition-colors">
          {title}
        </h3>
        
        <p className={`text-sm text-slate-400 leading-relaxed ${tall ? 'md:text-base' : ''}`}>
          {description}
        </p>

        {tall && (
          <div className="mt-6 pt-6 border-t border-white/5">
            <div className="space-y-3">
              {['If/Then Branches', 'Loop Iterations', 'Error Handling'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Corner decoration */}
      <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </motion.div>
  );
};

const FeaturesBento = () => {
  return (
    <section
      data-testid="features-bento"
      id="platform"
      className="relative py-16 lg:py-24 px-4 sm:px-6"
    >
      {/* Background effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-emerald-500/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-0 w-[150px] sm:w-[300px] h-[150px] sm:h-[300px] bg-cyan-500/5 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10 lg:mb-16"
        >
          <span className="uppercase tracking-[0.15em] sm:tracking-[0.2em] text-xs font-semibold text-emerald-400 mb-4 block">
            Platform Capabilities
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-outfit text-white mb-4">
            Everything You Need to <span className="text-gradient">Automate</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            From simple tasks to complex multi-agent orchestration, Vrittaa AI scales with your needs.
          </p>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesBento;
